// Import React so we can use JSX
import React from "react";



// This is a base SVG component to keep all icons consistent
export const GlobalIconBase = ({ 
    
    // This is the icon size
    size = 24,

    // This is the icon size
    viewBox = "0, 0, 24, 24",

    // This is an optional className
    className = "",

    // This is an optional accessible title
    title = "",

    // This is the svg children
    children,

    // This allows overriding stroke width when needed
    strokeWidth = 1, 

    // Fill colour
    fillColour = "none",

}) => {

    // This determines if we should expose the icon to screen readers
    const hasTitle = Boolean(title);

    // Return the SVG element that wraps any specific icon paths
    return (

        // SVG root element with configurable size and shared viewBox
        <svg
            // Allow passing custom classes for styling (BEM etc.)
            className={className}
            // Width of the icon, defaulting to the size prop
            width={size}
            // Height of the icon, defaulting to the size prop
            height={size}
            // Standard 24x24 icon coordinate grid
            viewBox={viewBox}
            // No fill by default; we mostly use strokes
            fill={fillColour}
            // Default stroke colour follows current text colour
            stroke="currentColor"
            // Default stroke thickness
            strokeWidth={strokeWidth}
            // Default rounded line ends
            strokeLinecap="round"
            // Default rounded joins
            strokeLinejoin="round"
            // XML namespace required for SVG
            xmlns="http://www.w3.org/2000/svg"
            // This hides from screen readers if no title
            aria-hidden={hasTitle ? undefined : "true"}
            // This applies role if title exists
            role={hasTitle ? "img" : undefined}
        >

            {/* This renders a title for accessibility if provided */}
            {hasTitle ? <title>{title}</title> : null}

            {/* Render the icon-specific shapes passed as children */}
            {children}

        </svg>

    );

};



// CLOSE BUTTON ICON (on pop ups)
export const CloseIcon = ({ size = 10, className = "" }) => {

    // Return the close icon using the shared base component
    return (

        <GlobalIconBase size={size} className={className}>
            
            {/* Draw the first diagonal line */}
            <line x1="3" y1="3" x2="21" y2="21" strokeWidth="5" strokeLinecap="round" />

            {/* Draw the second diagonal line */}
            <line x1="21" y1="3" x2="3" y2="21" strokeWidth="5" strokeLinecap="round" />
            
        </GlobalIconBase>

    );

};


// EDIT PEN ICON (on images)
export const EditPenIcon = ({ size = 15, className = "" }) => {
    
    return (

        <GlobalIconBase size={size} className={className}>
            
            {/* Draw the pencil body */}
            <path
                // Define the pencil shape
                d="M4 17.25V20h2.75L17.81 8.94l-2.75-2.75L4 17.25Z"
                // Use the surrounding text colour
                fill="currentColor"
            />

            {/* Draw the top section of the pencil */}
            <path
                // Define the pencil cap shape
                d="M19.71 7.04a1 1 0 0 0 0-1.41l-1.34-1.34a1 1 0 0 0-1.41 0l-1.05 1.05 2.75 2.75 1.05-1.05Z"
                // Use the surrounding text colour
                fill="currentColor"
            />
            
        </GlobalIconBase>

    );

};


// ARROW DOWN COLLAPSE ICON
export const ArrowDownCollapseIcon = ({ size = 15, className = "" }) => {
    
    return (

        <GlobalIconBase size={size} className={className} strokeWidth={0} fillColour="currentColor">
            
            {/* Draw the centred downward triangle */}
            <path d="M5.5 8.5 H18.5 L12 16 Z" />
            
        </GlobalIconBase>

    );

};


// ARROW UP COLLAPSE ICON
export const ArrowUpCollapseIcon = ({ size = 15, className = "" }) => {
    
    return (

        <GlobalIconBase size={size} className={className} strokeWidth={0} fillColour="currentColor">
            
            {/* Draw the centred upward triangle */}
            <path d="M5.5 15.5 H18.5 L12 8 Z" />
            
        </GlobalIconBase>

    );

};

   


// DASHBOARD ICON (homepage – grid of widgets)
export const DashboardIcon = ({ size = 24, className = "" }) => {

    // Return the Dashboard icon using the shared base component
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Top-left panel */}
            <rect
                // X position of the rectangle
                x="3"
                // Y position of the rectangle
                y="3"
                // Width of the rectangle
                width="8"
                // Height of the rectangle
                height="7"
                // Rounded corners
                rx="2"
                // Outline colour follows current text colour
                stroke="currentColor"
                // Outline thickness
                strokeWidth="2"
            />

            {/* Top-right panel */}
            <rect
                x="13"
                y="3"
                width="8"
                height="5"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-left panel */}
            <rect
                x="3"
                y="12"
                width="6"
                height="9"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-right panel */}
            <rect
                x="11"
                y="12"
                width="10"
                height="9"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

        </GlobalIconBase>

    );

};



// ORDERS ICON (document with checkmark)
export const OrdersIcon = ({ size = 24, className = "" }) => {

    // Return the Orders icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Document outline with folded corner */}
            <path
                // Path for a document with a folded top-right corner
                d="M7 3H13L17 7V21H7V3Z"
                // Outline color
                stroke="currentColor"
                // Outline thickness
                strokeWidth="2"
                // Rounded joins
                strokeLinejoin="round"
            />

            {/* Fold line */}
            <path
                d="M13 3V7H17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
            />

            {/* Checkmark to indicate order */}
            <path
                d="M9 13L11 15L15 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// PRINT ON DEMAND DESIGNS ICON (t-shirt with a small star)
export const PodDesignsIcon = ({ size = 24, className = "" }) => {

    // Return the POD Designs icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* T-shirt outline */}
            <path
                d="M9 4L7 6L5 6.5V10L7 10V20H17V10L19 10V6.5L17 6L15 4H9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {/* Small design star in the centre of the shirt */}
            <path
                d="M12 9L12.6 10.4L14 11L12.6 11.6L12 13L11.4 11.6L10 11L11.4 10.4L12 9Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// AI IMAGE GENERATION ICON (sparkle + image frame)
export const AiGenerationIcon = ({ size = 24, className = "" }) => {

    // Return the AI Generation icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Image frame */}
            <rect
                x="4"
                y="6"
                width="11"
                height="11"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Small sun or circle inside the frame */}
            <circle
                cx="8"
                cy="10"
                r="1.5"
                stroke="currentColor"
                strokeWidth="1.5"
            />

            {/* Simple mountains shape inside the frame */}
            <path
                d="M6 15L9 12L12 15L14 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {/* Sparkle to represent AI / magic */}
            <path
                d="M18 4V6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <path
                d="M18 9V11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <path
                d="M16 7H18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            <path
                d="M19 7H21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// IMAGES ICON (classic photo – mountains and sun)
export const ImagesIcon = ({ size = 24, className = "" }) => {

    // Return the Images icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Outer image frame */}
            <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Sun / circle */}
            <circle
                cx="9"
                cy="10"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Mountains */}
            <path
                d="M5 17L10 12L14 16L17 13L21 17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// CONNECTIONS ICON (nodes connected by lines)
export const ConnectionsIcon = ({ size = 24, className = "" }) => {

    // Return the Connections icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Top node */}
            <circle
                cx="12"
                cy="4"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-left node */}
            <circle
                cx="6"
                cy="18"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-right node */}
            <circle
                cx="18"
                cy="18"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Connection from top to bottom-left */}
            <path
                d="M11 6L7 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Connection from top to bottom-right */}
            <path
                d="M13 6L17 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Connection between bottom nodes */}
            <path
                d="M8 18H16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// STORES ICON (simple shopfront)
export const StoresIcon = ({ size = 24, className = "" }) => {

    // Return the Stores icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Shop roof */}
            <path
                d="M4 8H20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Awning */}
            <path
                d="M5 8L6.5 12H17.5L19 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />

            {/* Shop outline */}
            <path
                d="M6 12V19H18V12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />

            {/* Door */}
            <path
                d="M10 19V15C10 14.45 10.45 14 11 14H13C13.55 14 14 14.45 14 15V19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />

        </GlobalIconBase>

    );

};



// MARKETPLACE ICON (shopping bag for the public marketplace)
export const MarketplaceIcon = ({ size = 24, className = "" }) => {

    // Return the Marketplace icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Shopping bag outline */}
            <path
                d="M6 9H18L19 20H5L6 9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            {/* Shopping bag handle */}
            <path
                d="M9 10V7C9 5.34 10.34 4 12 4C13.66 4 15 5.34 15 7V10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// BALANCE & TRANSACTIONS ICON (card with arrows)
export const BalanceTransactionsIcon = ({ size = 24, className = "" }) => {

    // Return the Balance and Transactions icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Card outline */}
            <rect
                x="3"
                y="6"
                width="18"
                height="12"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Card stripe */}
            <path
                d="M3 10H21"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Incoming arrow (credit) */}
            <path
                d="M8 16L6 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M6 14L4 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M6 14V18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Outgoing arrow (spend) */}
            <path
                d="M16 14L18 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M18 16L20 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M18 18V14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// ACCOUNT ICON (user in a circle – SVG version)
export const AccountIcon = ({ size = 24, className = "" }) => {

    // Return the Account icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Outer circle enclosing the user */}
            <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Head */}
            <circle
                cx="12"
                cy="10"
                r="3"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Shoulders / body */}
            <path
                d="M6 18C7.5 15.5 9.5 14 12 14C14.5 14 16.5 15.5 18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};

// SUPPORT ICON (customer support headset)
export const SupportIcon = ({ size = 24, className = "" }) => {

    // Return the Support icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Draw the headset band */}
            <path
                d="M5 13V11C5 7.1 8.1 4 12 4C15.9 4 19 7.1 19 11V13"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Draw the left earpiece */}
            <path
                d="M5 12H8V17H5V12Z"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Draw the right earpiece */}
            <path
                d="M16 12H19V17H16V12Z"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Draw the microphone arm */}
            <path
                d="M19 16C19 18.8 17.2 20 14 20H12"
                stroke="currentColor"
                strokeWidth="2"
            />

        </GlobalIconBase>

    );

};

// CUSTOM PRODUCTS ICON (t-shirt, no star – product to print on)
export const CustomProductsIcon = ({ size = 24, className = "" }) => {

    // Return the Custom Products icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* T-shirt outline indicating a printable product */}
            <path
                d="M9 4L7 6L5 6.5V10L7 10V20H17V10L19 10V6.5L17 6L15 4H9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

        </GlobalIconBase>

    );

};



// PRODUCTS ICON (generic box – could be any kind of product)
export const ProductsIcon = ({ size = 24, className = "" }) => {

    // Return the Products icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Front face of the box */}
            <rect
                x="4"
                y="7"
                width="16"
                height="12"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Top flap line */}
            <path
                d="M4 11H20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Left flap notch */}
            <path
                d="M8 7V11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Right flap notch */}
            <path
                d="M16 7V11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// MANAGE MMMs ICON (three connected circles – Materials, Methods, Modes)
export const ManageMMMsIcon = ({ size = 24, className = "" }) => {

    // Return the Manage MMMs icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Top circle */}
            <circle
                cx="12"
                cy="5"
                r="2.5"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-left circle */}
            <circle
                cx="7"
                cy="17"
                r="2.5"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Bottom-right circle */}
            <circle
                cx="17"
                cy="17"
                r="2.5"
                stroke="currentColor"
                strokeWidth="2"
            />

            {/* Link between top and bottom-left */}
            <path
                d="M10.7 7.3L8.3 14.7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Link between top and bottom-right */}
            <path
                d="M13.3 7.3L15.7 14.7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Link between bottom circles */}
            <path
                d="M9.5 17H14.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};



// PLANS ICON (subscription card with price lines)
export const PlansIcon = ({ size = 24, className = "" }) => {

    // Return the Plans icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Main pricing card outline */}
            <rect
                x="5"
                y="4"
                width="14"
                height="16"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
            />

            {/* Top badge / featured plan marker */}
            <path
                d="M9 4H15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Price circle */}
            <circle
                cx="12"
                cy="9"
                r="2"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
            />

            {/* First pricing/detail line */}
            <path
                d="M8.5 14H15.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Second pricing/detail line */}
            <path
                d="M8.5 17H13.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};

// PLANS ICON (stacked pricing cards)
export const _PlansIcon = ({ size = 24, className = "" }) => {

    // Return the Plans icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Back card */}
            <rect
                x="4"
                y="7"
                width="10"
                height="11"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                opacity="0.6"
            />

            {/* Front card */}
            <rect
                x="10"
                y="4"
                width="10"
                height="13"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
            />

            {/* Price / title line */}
            <path
                d="M12.5 8H17.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Feature line */}
            <path
                d="M12.5 11H17.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

            {/* Feature line */}
            <path
                d="M12.5 14H15.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
            />

        </GlobalIconBase>

    );

};






// THEME / VISUAL SETTINGS ICON (half sun / half moon)
export const ThemeSettingsIcon = ({ size = 24, className = "" }) => {

    // Return the Theme / Visual Settings icon
    return (

        <GlobalIconBase size={size} className={className} strokeWidth={0}>

            {/* This is the main palette body */}
            <path
                // Use currentColor so CSS controls the icon colour
                fill="currentColor"

                // Simple bold palette silhouette for small-size readability
                d="M12 2C6.48 2 2 6.03 2 11c0 4.42 3.72 8 8.31 8h1.19c.83 0 1.5-.67 1.5-1.5 0-.35-.12-.68-.35-.95-.34-.39-.54-.9-.54-1.46 0-1.21.98-2.19 2.19-2.19h1.95c3.18 0 5.75-2.57 5.75-5.75C22 4.3 17.52 2 12 2Z"
            />

            {/* This is the thumb hole cutout */}
            <circle
                // Horizontal centre
                cx="15.4"

                // Vertical centre
                cy="9.2"

                // Radius of the thumb hole
                r="2.1"

                // White cutout
                fill="#FFFFFF"
            />

            {/* This is the top paint dot */}
            <circle
                // Horizontal position
                cx="8"

                // Vertical position
                cy="7"

                // Dot radius
                r="1.15"

                // White paint well
                fill="#FFFFFF"
            />

            {/* This is the left paint dot */}
            <circle
                // Horizontal position
                cx="6.2"

                // Vertical position
                cy="11.2"

                // Dot radius
                r="1.15"

                // White paint well
                fill="#FFFFFF"
            />

            {/* This is the lower paint dot */}
            <circle
                // Horizontal position
                cx="8.4"

                // Vertical position
                cy="15.2"

                // Dot radius
                r="1.15"

                // White paint well
                fill="#FFFFFF"
            />

        </GlobalIconBase>

    );

};



// Export Colour Mode icon (Colour)
export const ColourIcon = (props) => (

    // This uses the shared base
    <GlobalIconBase

        // This spreads props
        {...props}

    >

        {/* This draws a colour wheel style circle */}
        <circle cx="12" cy="12" r="8" />

        {/* This draws spokes */}
        <path d="M12 4a8 8 0 0 1 8 8" />

        {/* This draws spokes */}
        <path d="M12 12l6-3" />

        {/* This draws spokes */}
        <path d="M12 12l0-7" />

        {/* This draws spokes */}
        <path d="M12 12l-6 3" />

    </GlobalIconBase>

);



// Export Colour Mode icon (Monochrome)
export const MonochromeIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase

        // This spreads props
        {...props}

    >

        {/* This draws a circle */}
        <circle cx="12" cy="12" r="8" />

        {/* This draws a diagonal split */}
        <path d="M8 8l8 8" />

        {/* This draws a half shading line */}
        <path d="M9 15a6 6 0 0 1 0-6" />

    </GlobalIconBase>

);



// Export Show All icon (eye)
export const ShowAllIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws an eye outline */}
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />

        {/* This draws the iris */}
        <circle cx="12" cy="12" r="2.5" />

    </GlobalIconBase>

);



// Export Assign All icon (checklist)
export const AssignAllIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws the list */}
        <path d="M9 6h12" />

        {/* This draws the list */}
        <path d="M9 12h12" />

        {/* This draws the list */}
        <path d="M9 18h12" />

        {/* This draws the checks */}
        <path d="M3.5 6l1.5 1.5L7.5 5" />

        {/* This draws the checks */}
        <path d="M3.5 12l1.5 1.5L7.5 11" />

        {/* This draws the checks */}
        <path d="M3.5 18l1.5 1.5L7.5 17" />

    </GlobalIconBase>

);



// Export Clear All Edited Products icon (eraser)
export const ClearAllEditedProductsIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws an eraser body */}
        <path d="M3 15l7-7a2 2 0 0 1 3 0l7 7a2 2 0 0 1 0 3l-2 2H8l-5-5a2 2 0 0 1 0-0z" />

        {/* This draws the eraser edge */}
        <path d="M10 20l6-6" />

    </GlobalIconBase>

);



// Export Locked icon (padlock)
export const LockedIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws lock shackle */}
        <path d="M8 11V8a4 4 0 1 1 8 0v3" />

        {/* This draws lock body */}
        <rect x="6" y="11" width="12" height="10" rx="2" />

        {/* This draws keyhole */}
        <path d="M12 15v3" />

    </GlobalIconBase>

);



// Export Warning (general) icon (triangle exclamation)
export const WarningIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws warning triangle */}
        <path d="M12 3l10 18H2L12 3z" />

        {/* This draws exclamation line */}
        <path d="M12 9v5" />

        {/* This draws exclamation dot */}
        <path d="M12 17h.01" />

    </GlobalIconBase>

);



// Export Image Quality Warning icon (image + exclamation)
export const ImageQualityWarningIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws image frame */}
        <rect x="4" y="5" width="14" height="14" rx="2" />

        {/* This draws mountain */}
        <path d="M6 16l4-4 3 3 2-2 3 3" />

        {/* This draws sun */}
        <circle cx="9" cy="9" r="1" />

        {/* This draws warning marker */}
        <path d="M20 8v5" />

        {/* This draws warning dot */}
        <path d="M20 15h.01" />

    </GlobalIconBase>

);



// Export Bleed Not Covered Warning icon (crop marks + exclamation)
export const BleedNotCoveredWarningIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws crop/bleed frame */}
        <path d="M7 3v4H3" />

        {/* This draws crop/bleed frame */}
        <path d="M17 3v4h4" />

        {/* This draws crop/bleed frame */}
        <path d="M7 21v-4H3" />

        {/* This draws crop/bleed frame */}
        <path d="M17 21v-4h4" />

        {/* This draws inner rectangle */}
        <rect x="7" y="7" width="10" height="10" rx="1" />

        {/* This draws warning marker */}
        <path d="M12 10v4" />

        {/* This draws warning dot */}
        <path d="M12 16h.01" />

    </GlobalIconBase>

);



// Export Position icon (print area positions)
export const PositionIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws a bounding box */}
        <rect x="4" y="4" width="16" height="16" rx="2" />

        {/* This draws a target dot */}
        <circle cx="12" cy="12" r="1.5" />

        {/* This draws guide lines */}
        <path d="M12 6v3" />

        {/* This draws guide lines */}
        <path d="M12 15v3" />

        {/* This draws guide lines */}
        <path d="M6 12h3" />

        {/* This draws guide lines */}
        <path d="M15 12h3" />

    </GlobalIconBase>

);



// Export Fit icon (contain)
export const FitIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws the frame */}
        <rect x="4" y="4" width="16" height="16" rx="2" />

        {/* This draws inner contained image */}
        <rect x="7" y="8" width="10" height="8" rx="1" />

        {/* This draws arrows indicating fitting */}
        <path d="M7 8l2 2" />

        {/* This draws arrows indicating fitting */}
        <path d="M17 8l-2 2" />

        {/* This draws arrows indicating fitting */}
        <path d="M7 16l2-2" />

        {/* This draws arrows indicating fitting */}
        <path d="M17 16l-2-2" />

    </GlobalIconBase>

);



// Export Fill icon (cover)
export const FillIcon  = (props) => (

    // This uses the shared base
    <GlobalIconBase {...props}>

        {/* This draws the frame */}
        <rect x="4" y="4" width="16" height="16" rx="2" />

        {/* This draws a filled area */}
        <rect x="6" y="7" width="12" height="10" rx="1" />

        {/* This draws corner expand arrows */}
        <path d="M8 9h2V7" />

        {/* This draws corner expand arrows */}
        <path d="M16 9h-2V7" />

        {/* This draws corner expand arrows */}
        <path d="M8 15h2v2" />

        {/* This draws corner expand arrows */}
        <path d="M16 15h-2v2" />

    </GlobalIconBase>

);


// FIT / FILL TOGGLE ICON (two squares to represent contain vs cover)
export const FitFillIcon = ({ size = 24, className = "", title = "Fit / Fill" }) => {

    // Return the Fit/Fill icon
    return (

        <GlobalIconBase size={size} className={className} title={title}>

            {/* This is the outer frame */}
            <rect x="4" y="4" width="16" height="16" rx="2" />

            {/* This is the "fit" inner box (contain) */}
            <rect x="7" y="8" width="10" height="8" rx="1" opacity="0.65" />

            {/* This is the "fill" overlay hint (cover) */}
            <path d="M6 7h12v10H6z" opacity="0.25" />

        </GlobalIconBase>

    );

};





















// NO AI STYLE SELECTED ICON - sparkles inside a soft placeholder frame
export const NoAiStyleSelectedIcon = ({ size = 24, className = "" }) => {

    // Return the icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Outer placeholder frame */}
            <rect
                x="4"
                y="4"
                width="16"
                height="16"
                rx="3"
                stroke="currentColor"
                strokeWidth="0.5"
                strokeDasharray="2 2"
                fill="none"
            />

            {/* Main sparkle */}
            <path
                d="M12 7L12.8 9.2L15 10L12.8 10.8L12 13L11.2 10.8L9 10L11.2 9.2L12 7Z"
                stroke="currentColor"
                strokeWidth="0.6"
                strokeLinejoin="round"
                fill="none"
            />

            {/* Small top-left sparkle */}
            <path
                d="M8 7.5L8.3 8.2L9 8.5L8.3 8.8L8 9.5L7.7 8.8L7 8.5L7.7 8.2L8 7.5Z"
                stroke="currentColor"
                strokeWidth="0.3"
                strokeLinejoin="round"
                fill="none"
            />

            {/* Small bottom-right sparkle */}
            <path
                d="M16 13.5L16.3 14.2L17 14.5L16.3 14.8L16 15.5L15.7 14.8L15 14.5L15.7 14.2L16 13.5Z"
                stroke="currentColor"
                strokeWidth="0.3"
                strokeLinejoin="round"
                fill="none"
            />

        </GlobalIconBase>

    );

};


// NO AI STYLE SELECTED ICON - palette with question mark
export const NoAiStylePaletteIcon = ({ size = 24, className = "" }) => {

    // Return the icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Palette outer shape */}
            <path
                d="M12 4C7.6 4 4 7.1 4 11.1C4 14.5 6.7 17 10 17H11.2C12 17 12.5 17.5 12.5 18.2C12.5 19.2 13.3 20 14.4 20C17.9 20 20 16.8 20 13C20 8 16.3 4 12 4Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
                fill="none"
            />

            {/* Palette paint spots */}
            <circle
                cx="8.2"
                cy="10"
                r="1"
                fill="currentColor"
            />

            <circle
                cx="10.8"
                cy="8.2"
                r="1"
                fill="currentColor"
            />

            <circle
                cx="13.7"
                cy="8.8"
                r="1"
                fill="currentColor"
            />

            {/* Question mark */}
            <path
                d="M14.2 12.2C14.2 11.1 13.4 10.4 12.3 10.4C11.4 10.4 10.7 10.8 10.1 11.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
            />

            <path
                d="M12.2 12.6V13.7"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
            />

            <circle
                cx="12.2"
                cy="16"
                r="0.8"
                fill="currentColor"
            />

        </GlobalIconBase>

    );

};


// NO AI STYLE SELECTED ICON - empty image frame with sparkles
export const NoAiStyleImageFrameIcon = ({ size = 24, className = "" }) => {

    // Return the icon
    return (

        <GlobalIconBase size={size} className={className}>

            {/* Image frame */}
            <rect
                x="4"
                y="5"
                width="16"
                height="14"
                rx="2"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
            />

            {/* Frame corner sun/dot */}
            <circle
                cx="9"
                cy="10"
                r="1.2"
                stroke="currentColor"
                strokeWidth="1.4"
                fill="none"
            />

            {/* Lower image shape */}
            <path
                d="M6.5 16L10.2 12.5L12.8 14.7L15 12.8L17.5 16"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
            />

            {/* Small sparkle above frame */}
            <path
                d="M17 3.8L17.4 4.9L18.5 5.3L17.4 5.7L17 6.8L16.6 5.7L15.5 5.3L16.6 4.9L17 3.8Z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
                fill="none"
            />

        </GlobalIconBase>

    );

};



// Made in illustrator
export const MoveArrows = ({ size = 30, viewBox = "0, 0, 145, 145", fillColour = "none", className = "" }) => {

    // Return the icon
    return (

        <GlobalIconBase size={size} className={className} viewBox={viewBox} fillColour={fillColour}>

            <path d="M34.14,55.39l-9.2,8.47,0.2,0.72H28c5.41,0,10.83,0,16.24,0,3.32,0,4.16.88,4.19,4.25,0.1,8.65.33,8.35-8.17,8.27-5,0-10,0-16,0,2.53,2.54,4.5,4.49,6.44,6.47,2.46,2.5,2.48,3.52.07,5.93-6.31,6.32-5.6,6.26-11.81,0C13.89,84.31,8.7,79.15,3.54,74,1,71.44,1,70.26,3.44,67.81Q12.69,58.52,22,49.27c2.44-2.43,3.45-2.41,5.94,0C29.83,51.12,31.71,53,34.14,55.39Z"/>
            
            <path d="M116.47,77.1c-6.25,0-12.5,0-18.75,0-3.69,0-4.42-.79-4.47-4.55-0.11-8.35-.12-8,7.92-8,5.11,0,10.21,0,16.22,0-2.6-2.61-4.64-4.64-6.65-6.7-2.18-2.24-2.19-3.32,0-5.52,1.26-1.29,2.56-2.55,3.86-3.8a2.89,2.89,0,0,1,4.52,0q10,10,20,20a2.93,2.93,0,0,1,0,4.54q-9.95,10-20,20c-1.46,1.46-3,1.45-4.52,0l-0.21-.19c-6.73-6.68-7.17-5.75-.37-12.35a32.59,32.59,0,0,1,3.15-2.41Z"/>
            
            <path d="M64,24.61l-6.3,6.6C56,33,54.27,33,52.54,31.19c-1.24-1.31-2.53-2.58-3.8-3.86-1.68-1.69-1.56-3.3.1-4.94Q58.54,12.73,68.2,3c1.86-1.87,3.58-1.77,5.4.06q9.55,9.61,19.16,19.16c1.89,1.87,1.81,3.53,0,5.37-6.42,6.46-5.69,6.76-12,.28a32.7,32.7,0,0,1-2.55-3.39l-1.1.6q0,1.47,0,2.94c0,5.61,0,11.22,0,16.83,0,2.37-1.16,3.55-3.46,3.57-9.42.07-9.19,0.7-9.08-8.22,0.06-4.85,0-9.7,0-14.55A4.92,4.92,0,0,0,64,24.61Z"/>
            
            <path d="M64.6,116.37c0-6.27,0-12.54,0-18.8,0-3.5.84-4.29,4.39-4.34,8.67-.12,8.19-0.07,8.12,8,0,5.08,0,10.16,0,15.24l1,0.65c1.92-2.18,3.83-4.37,5.76-6.54,1.74-1.95,3.48-2,5.35-.07,6.54,6.66,6.71,5.74.15,12.24-5.2,5.15-10.35,10.36-15.54,15.51-2.37,2.35-3.63,2.35-6,0q-9.4-9.35-18.75-18.76c-2.21-2.22-2.19-3.29-.07-5.53,1.18-1.24,2.41-2.43,3.6-3.66,1.66-1.7,3.3-1.64,4.94,0l6.34,6.39Z"/>

        </GlobalIconBase>

    );

};






// Reusable moderation status icon
export default function ModerationIcon({
    status,
    size = 16,
    className = ""
}) {

    // Define the icon paths for each moderation status
    const paths = {
        approved: (
            <path
                d="M5 12l4 4L19 6"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        ),
        rejected: (
            <path
                d="M7 7l10 10M17 7L7 17"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
            />
        ),
        pending: (
            <circle
                cx="12"
                cy="12"
                r="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="3 3"
            />
        )
    };

    // Return the moderation icon
    return (

        <svg
            // Set the icon dimensions
            width={size}
            height={size}
            // Keep the drawing proportional at any size
            viewBox="0 0 24 24"
            // Apply the status-specific CSS class
            className={`pod-moderation__icon pod-moderation__icon--${status} ${className}`}
            // Prevent duplicate accessibility announcements
            aria-hidden="true"
            // Prevent SVG focus in older browsers
            focusable="false"
        >
            {/* Draw the background for approved and rejected icons */}
            {status !== "pending" && (
                <circle
                    cx="12"
                    cy="12"
                    r="11"
                    fill="currentColor"
                />
            )}

            {/* Draw the appropriate status symbol */}
            {paths[status]}

        </svg>

    );
    
}